/**
 * PsbStage5Page — Route B YTD Baseline & Audit History
 *
 * Step 1: Source   — select applied Route B S2 session; cross-check fingerprints
 * Step 2: Matrix   — completeness matrix (per-ref HIDA/SUMM/HPLU coverage) + dry-run confirm
 * Step 3: Review   — 4 tabs: HIDA timeline, SUMM periods, HPLU products, BARSFILE YTD
 * Step 4: Apply    — apply plan + session recalc-check evidence gate
 *
 * URL persistence: ?planId=<uuid> via React Router useSearchParams
 * Back nav: deterministic (never window.history)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ChevronRight, ClipboardList, Database,
  Loader2, RefreshCw, ShieldAlert, BarChart2, AlertTriangle, Info,
  TrendingUp, Calendar, Package, Table2, BookOpen,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../services/api';

// ── Constants ──────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Source' },
  { id: 2, label: 'Matrix' },
  { id: 3, label: 'Review' },
  { id: 4, label: 'Apply' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtGBP(v) {
  if (v == null || v === '' || v === 'None') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  const sign = n < 0 ? '−' : '';
  return `${sign}£${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtN(v, dp = 4) {
  if (v == null || v === '' || v === 'None') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(dp);
}

function fmtDate(s) {
  if (!s) return '—';
  return s;
}

// ── Step nav ──────────────────────────────────────────────────────────────────

function StepNav({ current }) {
  return (
    <ol className="flex items-center gap-2 text-sm mb-6" data-testid="step-nav-s5">
      {STEPS.map((s, idx) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <React.Fragment key={s.id}>
            <li
              data-testid={`step-nav-s5-${s.id}`}
              className={`flex items-center gap-1.5 font-medium ${active ? 'text-slate-900' : done ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              {done
                ? <CheckCircle2 size={15} className="text-emerald-500" />
                : <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs border ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-400'}`}>{s.id}</span>
              }
              {s.label}
            </li>
            {idx < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cls = {
    planned:  'bg-amber-50 text-amber-700 border-amber-200',
    applied:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    complete: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    partial:  'bg-amber-50 text-amber-700 border-amber-200',
    hida_only:'bg-slate-100 text-slate-500 border-slate-200',
    current_period_skip: 'bg-purple-50 text-purple-700 border-purple-200',
  }[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <Badge variant="outline" className={`text-xs font-medium ${cls}`}>
      {status?.replace(/_/g, ' ')}
    </Badge>
  );
}

// ── Source session banner ─────────────────────────────────────────────────────

function SourceBanner({ session }) {
  if (!session) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 mb-4" data-testid="s5-source-banner">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
        <span><span className="font-semibold">Session:</span> {session.session_reference}</span>
        <span><span className="font-semibold">Route:</span> B — Historic Draft</span>
        <span><span className="font-semibold">Site:</span> {session.target_site_id}</span>
        <span><span className="font-semibold">Period:</span> {session.period_from} → {session.period_to}</span>
        <span><span className="font-semibold">Stage 0:</span> <span className="font-mono">{session.stage0_run_id?.slice(0, 12)}…</span></span>
        <span><span className="font-semibold">S2 Plan:</span> <span className="font-mono">{session.s2_plan_id?.slice(0, 8)}</span></span>
      </div>
    </div>
  );
}

// ── Bridge summary card ───────────────────────────────────────────────────────

function BridgeSummary({ bridge }) {
  if (!bridge) return null;
  const items = [
    { label: 'HIDA periods', value: bridge.hida_refs_total, sub: `refs ${bridge.hida_refs_min}–${bridge.hida_refs_max}` },
    { label: 'SUMM rows', value: bridge.summ_refs_total, sub: `refs ${bridge.summ_refs_min}–${bridge.summ_refs_max}` },
    { label: 'HPLU product rows', value: bridge.hplu_product_rows_total?.toLocaleString(), sub: `${bridge.hplu_refs_total} refs${bridge.hplu_duplicate_source_rows > 0 ? ` · ${bridge.hplu_unique_keys?.toLocaleString()} unique keys (${bridge.hplu_duplicate_source_rows} dup src rows)` : ''}` },
    { label: 'BARSFILE YTD bars', value: bridge.barsfile_bars, sub: `FYTD date: ${bridge.barsfile_fytddate}` },
    { label: 'Current ref (skip)', value: bridge.current_ref, sub: 'AbbeyZone session takes precedence' },
    { label: 'Complete (H+S+P)', value: bridge.refs_complete, sub: `${bridge.refs_partial} partial, ${bridge.refs_hida_only} HIDA-only` },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4" data-testid="s5-bridge-summary">
      {items.map(({ label, value, sub }) => (
        <div key={label} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="text-xl font-bold text-slate-900">{value ?? '—'}</div>
          <div className="text-xs font-medium text-slate-700">{label}</div>
          {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ── Completeness Matrix table ─────────────────────────────────────────────────

function CompletenessMatrix({ bridge }) {
  const [showAll, setShowAll] = useState(false);
  if (!bridge?.per_ref) return null;
  const rows = showAll ? bridge.per_ref : bridge.per_ref.slice(-20);

  return (
    <div data-testid="s5-completeness-matrix">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-800">Per-Ref Completeness Matrix</h3>
        {bridge.per_ref.length > 20 && (
          <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setShowAll(v => !v)}>
            {showAll ? `Show last 20 of ${bridge.per_ref.length}` : `Show all ${bridge.per_ref.length} refs`}
          </Button>
        )}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs" data-testid="s5-matrix-table">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Ref</th>
              <th className="px-3 py-2 text-left">Open Date</th>
              <th className="px-3 py-2 text-left">Close Date</th>
              <th className="px-3 py-2 text-center">HIDA</th>
              <th className="px-3 py-2 text-center">SUMM</th>
              <th className="px-3 py-2 text-center">HPLU</th>
              <th className="px-3 py-2 text-right">HPLU Products</th>
              <th className="px-3 py-2 text-right">Takings Exc</th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr
                key={r.ref}
                className={`hover:bg-slate-50 ${r.metric_status === 'current_period_skip' ? 'bg-purple-50' : ''}`}
                data-testid={`s5-matrix-row-${r.ref}`}
              >
                <td className="px-3 py-1.5 font-mono font-medium">{r.ref}</td>
                <td className="px-3 py-1.5 text-slate-500">{fmtDate(r.opendate)}</td>
                <td className="px-3 py-1.5 text-slate-500">{fmtDate(r.closdate)}</td>
                <td className="px-3 py-1.5 text-center">{r.in_hida ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-1.5 text-center">{r.in_summ ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-1.5 text-center">{r.in_hplu ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-1.5 text-right font-mono">{r.hplu_count > 0 ? r.hplu_count : '—'}</td>
                <td className="px-3 py-1.5 text-right font-mono">{r.summ_takeexc ? fmtGBP(r.summ_takeexc) : '—'}</td>
                <td className="px-3 py-1.5 text-center"><StatusBadge status={r.metric_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!showAll && bridge.per_ref.length > 20 && (
        <p className="text-xs text-slate-400 mt-1 text-right">Showing last 20 of {bridge.per_ref.length} refs. Use "Show all" to expand.</p>
      )}
    </div>
  );
}

// ── HIDA timeline tab ─────────────────────────────────────────────────────────

function HidaTab({ planId, totalCount }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(totalCount || 0);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 64;

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/psb-stage5/plans/${planId}?tab=hida&page=${p}&page_size=${PAGE_SIZE}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load HIDA rows'); }
    finally { setLoading(false); }
  }, [planId]);

  useEffect(() => { if (planId) load(page); }, [planId, page, load]);

  return (
    <div data-testid="s5-hida-tab">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-500">{total} historical audit periods (refs 3–66)</p>
        {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Legacy Ref</th>
              <th className="px-3 py-2 text-left">Open Date</th>
              <th className="px-3 py-2 text-left">Close Date</th>
              <th className="px-3 py-2 text-right">Days</th>
              <th className="px-3 py-2 text-left">House</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr key={r.legacy_ref} className="hover:bg-slate-50" data-testid={`s5-hida-row-${r.legacy_ref}`}>
                <td className="px-3 py-1.5 font-mono font-semibold">{r.legacy_ref}</td>
                <td className="px-3 py-1.5">{r.opendate}</td>
                <td className="px-3 py-1.5">{r.closdate}</td>
                <td className="px-3 py-1.5 text-right">{r.days}</td>
                <td className="px-3 py-1.5 text-slate-400">{r.house}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── SUMM periods tab ──────────────────────────────────────────────────────────

function SummTab({ planId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/psb-stage5/plans/${planId}?tab=summ&page=1&page_size=50`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load SUMM rows'); }
    finally { setLoading(false); }
  }, [planId]);

  useEffect(() => { if (planId) load(); }, [planId, load]);

  return (
    <div data-testid="s5-summ-tab">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-500">{total} period-bar summaries (refs 51–66) — takings stored as evidence only (Stage 6 boundary)</p>
        {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Ref</th>
              <th className="px-3 py-2 text-left">Bar</th>
              <th className="px-3 py-2 text-left">Open</th>
              <th className="px-3 py-2 text-left">Close</th>
              <th className="px-3 py-2 text-right">Days</th>
              <th className="px-3 py-2 text-right">VAT%</th>
              <th className="px-3 py-2 text-right">Take Exc (evidence)</th>
              <th className="px-3 py-2 text-right">Take Inc (evidence)</th>
              <th className="px-3 py-2 text-center">Stage 6</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr key={`${r.legacy_ref}-${r.bar}`} className="hover:bg-slate-50" data-testid={`s5-summ-row-${r.legacy_ref}`}>
                <td className="px-3 py-1.5 font-mono font-semibold">{r.legacy_ref}</td>
                <td className="px-3 py-1.5">{r.bar}</td>
                <td className="px-3 py-1.5">{r.opendate}</td>
                <td className="px-3 py-1.5">{r.closdate}</td>
                <td className="px-3 py-1.5 text-right">{r.days}</td>
                <td className="px-3 py-1.5 text-right">{r.vat}</td>
                <td className="px-3 py-1.5 text-right font-mono text-amber-700">{fmtGBP(r.takeexc)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-amber-700">{fmtGBP(r.takeinc)}</td>
                <td className="px-3 py-1.5 text-center">
                  {r.stage6_boundary && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600 border-amber-200">boundary</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── HPLU products tab ─────────────────────────────────────────────────────────

function HpluTab({ planId, bridge }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refFilter, setRefFilter] = useState('');
  const PAGE_SIZE = 100;

  const availableRefs = bridge?.per_ref
    ?.filter(r => r.in_hplu)
    .map(r => r.ref)
    .sort((a, b) => a - b) || [];

  const load = useCallback(async (p, ref) => {
    setLoading(true);
    try {
      const refParam = ref ? `&ref_filter=${ref}` : '';
      const r = await api.get(`/admin/psb-stage5/plans/${planId}?tab=hplu&page=${p}&page_size=${PAGE_SIZE}${refParam}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load HPLU rows'); }
    finally { setLoading(false); }
  }, [planId]);

  useEffect(() => {
    if (planId) load(1, refFilter || undefined);
    setPage(1);
  }, [planId, refFilter, load]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    load(newPage, refFilter || undefined);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="s5-hplu-tab">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <p className="text-xs text-slate-500 flex-1">
          {total.toLocaleString()} per-product history rows (refs {bridge?.hplu_refs_min}–{bridge?.hplu_refs_max}) — read-only legacy evidence
        </p>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 font-medium">Filter by Ref:</label>
          <select
            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
            value={refFilter}
            onChange={e => setRefFilter(e.target.value)}
            data-testid="s5-hplu-ref-filter"
          >
            <option value="">All refs ({bridge?.hplu_product_rows_total?.toLocaleString()})</option>
            {availableRefs.map(ref => (
              <option key={ref} value={ref}>Ref {ref}</option>
            ))}
          </select>
          {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Ref</th>
              <th className="px-3 py-2 text-left">Item Code</th>
              <th className="px-3 py-2 text-left">Bar</th>
              <th className="px-3 py-2 text-right">Bar Price</th>
              <th className="px-3 py-2 text-right">Till Total</th>
              <th className="px-3 py-2 text-right">Actual</th>
              <th className="px-3 py-2 text-right">Allow</th>
              <th className="px-3 py-2 text-right">Sell</th>
              <th className="px-3 py-2 text-right">Cost</th>
              <th className="px-3 py-2 text-right">Diff Sell</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`s5-hplu-row-${r.legacy_ref}-${r.itemcode}`}>
                <td className="px-3 py-1.5 font-mono font-semibold">{r.legacy_ref}</td>
                <td className="px-3 py-1.5 font-mono">{r.itemcode}</td>
                <td className="px-3 py-1.5">{r.bar}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(r.barprice)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtN(r.tilltl, 2)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtN(r.actual, 2)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtN(r.allow, 2)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(r.sell)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(r.cost)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(r.diffsell)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span>Page {page} of {totalPages} ({total.toLocaleString()} rows)</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>Prev</Button>
            <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── BARSFILE YTD tab ──────────────────────────────────────────────────────────

function BarsfileTab({ planId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/psb-stage5/plans/${planId}?tab=barsfile&page=1&page_size=10`);
      setRows(r.data.rows || []);
    } catch { toast.error('Failed to load BARSFILE rows'); }
    finally { setLoading(false); }
  }, [planId]);

  useEffect(() => { if (planId) load(); }, [planId, load]);

  return (
    <div data-testid="s5-barsfile-tab">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={14} className="text-emerald-600" />
        <p className="text-xs text-slate-600 font-medium">BARSFILE YTD Baseline — current period bar data with Financial Year-to-Date accumulators</p>
        {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      {rows.map(r => (
        <div key={r.bar} className="space-y-4" data-testid="s5-barsfile-row">
          {/* YTD Baseline */}
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm font-semibold text-emerald-800">
                Bar {r.bar} — {r.barname} | FYTD Baseline from {r.fytddate}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { label: 'FYTD Gross Profit', value: fmtGBP(r.fytdgrospr), highlight: true },
                  { label: 'FYTD Result', value: fmtGBP(r.fytdresult) },
                  { label: 'FYTD Takings Exc (evidence)', value: fmtGBP(r.fytdtakexc), warn: true },
                  { label: 'FYTD Takings Inc (evidence)', value: fmtGBP(r.fytdtakinc), warn: true },
                  { label: 'FYTD Adjustment', value: fmtGBP(r.fytdadjust) },
                  { label: 'FYTD Allowances', value: fmtGBP(r.fytdallowa) },
                  { label: 'FYTD Barrel', value: fmtGBP(r.fytdbarrel) },
                ].map(({ label, value, highlight, warn }) => (
                  <div key={label} className="bg-white rounded p-2.5 border border-slate-200">
                    <div className={`text-sm font-bold ${highlight ? 'text-emerald-700' : warn ? 'text-amber-700' : 'text-slate-800'}`}>{value}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                    {warn && <div className="text-xs text-amber-500 mt-0.5">Stage 6 boundary</div>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          {/* Current period cost bridge */}
          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm font-medium text-slate-700">Current Period Cost Bridge</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Opening Cost Total', value: fmtGBP(r.opencosttl), note: 'Bridges Stage 2' },
                  { label: 'Purchase Cost Total', value: fmtGBP(r.purccosttl), note: 'Bridges Stage 3' },
                  { label: 'Consumed Cost Total', value: fmtGBP(r.conscosttl) },
                  { label: 'Credit Cost Total', value: fmtGBP(r.credcosttl) },
                  { label: 'Current Takings Exc', value: fmtGBP(r.takeexc), warn: true },
                  { label: 'Current Takings Inc', value: fmtGBP(r.takeinc), warn: true },
                ].map(({ label, value, note, warn }) => (
                  <div key={label} className={`rounded p-2.5 border ${warn ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="text-sm font-bold text-slate-800">{value}</div>
                    <div className="text-xs text-slate-500">{label}</div>
                    {note && <div className="text-xs text-emerald-600">{note}</div>}
                    {warn && <div className="text-xs text-amber-500">Stage 6 boundary</div>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
}

// ── Recalc check card ─────────────────────────────────────────────────────────

function RecalcCheckCard({ planId, onComplete }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [polling, setPolling] = useState(false);

  const poll = async () => {
    try {
      const r = await api.get(`/admin/psb-stage5/plans/${planId}/recalc-result`);
      const d = r.data;
      if (d.job_status === 'complete') {
        setResult(d);
        setPolling(false);
        if (d.stable) {
          toast.success('Recalc check passed — Stage 5 history rows unchanged');
          onComplete?.(d);
        } else {
          toast.error('Recalc check failed — SHA mismatch detected');
        }
      }
    } catch { /* ignore poll errors */ }
  };

  useEffect(() => {
    if (!polling) return;
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, planId]);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      // Trigger background recalcs, returns immediately with last known result
      const r = await api.post(`/admin/psb-stage5/plans/${planId}/session-recalc-check`);
      const d = r.data;
      if (d.job_status === 'complete') {
        // Already done from memory cache
        setResult(d);
        if (d.stable) {
          toast.success('Recalc check passed — Stage 5 history rows unchanged');
          onComplete?.(d);
        }
      } else {
        // Start polling
        toast.info('Recalcs started in background (~15 s). Polling for result…');
        setPolling(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recalc check failed');
    }
    setRunning(false);
  };

  const stable = result?.stable;
  const border = stable === true ? 'border-emerald-300 bg-emerald-50/40'
    : stable === false ? 'border-red-300 bg-red-50/20'
    : 'border-slate-200';

  return (
    <Card className={`border-2 ${border}`} data-testid="s5-recalc-check-card">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldAlert size={14} className={stable ? 'text-emerald-600' : 'text-slate-400'} />
          Session Recalc Check (Evidence Gate)
          {polling && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded animate-pulse">polling…</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        <p className="text-xs text-slate-600">
          Runs <code className="bg-slate-100 px-1 rounded">calculate_valuation</code> on session {' '}
          <span className="font-mono font-medium">{result?.session_id || '…'}</span> twice and
          proves Stage 5 history rows are completely unchanged.
          {polling && <span className="ml-1 text-blue-600"> Recalcs running in background (~15 s)…</span>}
        </p>
        {result && (
          <div className="space-y-2" data-testid="s5-recalc-result">
            <div className={`flex items-center gap-2 text-sm font-semibold ${stable ? 'text-emerald-700' : 'text-red-700'}`}>
              {stable ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
              {stable ? 'STABLE — History rows unchanged after two recalculations' : 'UNSTABLE — SHA mismatch detected'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              {[
                { label: 'SHA Before', value: result.sha_before, ok: true },
                { label: 'SHA After Recalc 1', value: result.sha_after_recalc_1, ok: result.recalc_1_stable },
                { label: 'SHA After Recalc 2', value: result.sha_after_recalc_2, ok: result.recalc_2_stable },
              ].map(({ label, value, ok }) => (
                <div key={label} className={`rounded p-2 border font-mono ${ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="font-semibold font-sans text-slate-600 mb-0.5 non-italic">{label}</div>
                  {value}
                </div>
              ))}
            </div>
            {(result.ytd_sha_before || result.ytd_sha_after_recalc_1) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                {[
                  { label: 'YTD SHA Before', value: result.ytd_sha_before, ok: true },
                  { label: 'YTD SHA After Recalc 1', value: result.ytd_sha_after_recalc_1, ok: result.recalc_1_ytd_stable },
                  { label: 'YTD SHA After Recalc 2', value: result.ytd_sha_after_recalc_2, ok: result.recalc_2_ytd_stable },
                ].map(({ label, value, ok }) => (
                  <div key={label} className={`rounded p-2 border font-mono ${ok === false ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'}`}>
                    <div className="font-semibold font-sans text-slate-600 mb-0.5 non-italic">{label}</div>
                    {value || '—'}
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
              {Object.entries(result.row_counts_before || {}).filter(([k]) => k.endsWith('_count')).map(([k, v]) => (
                <div key={k} className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="font-bold text-slate-800">{String(v)}</div>
                  <div className="text-slate-500">{k.replace('_count', '')}</div>
                </div>
              ))}
            </div>
            {result.recalc_1_error && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                <span className="font-semibold">Recalc 1 note:</span> {result.recalc_1_error}
              </div>
            )}
            {result.recalc_2_error && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                <span className="font-semibold">Recalc 2 note:</span> {result.recalc_2_error}
              </div>
            )}
            {result.completed_at && (
              <div className="text-xs text-slate-500">
                Completed at: {result.completed_at}
              </div>
            )}
            <div className="text-xs text-emerald-700 bg-emerald-50 rounded p-2 border border-emerald-200 italic">
              {result.evidence || 'psb_s5_* tables isolated from calculate_valuation; session_previous_ytd preserved via source_type=psb_s5 sentinel.'}
            </div>
          </div>
        )}
        {(!result || result.job_status !== 'complete') && !polling && (
          <Button
            className="w-full mt-2"
            data-testid="s5-run-recalc-check-btn"
            onClick={run}
            disabled={running || polling}
          >
            {running ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
            Run Session Recalc × 2
          </Button>
        )}
        {polling && !result && (
          <div className="flex items-center justify-center gap-2 text-xs text-blue-600 py-2">
            <Loader2 size={14} className="animate-spin" />
            Recalculations running in background… polling every 4 s
          </div>
        )}
        {result?.stable && (
          <div className="flex items-center gap-2 text-xs text-emerald-700 font-medium mt-1">
            <CheckCircle2 size={14} />
            Evidence gate passed — ytd_stable: {String(result.ytd_stable)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── YTD Write Status Card ─────────────────────────────────────────────────────

function YtdWriteCard({ planId, sessionId }) {
  const [ytd, setYtd] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!planId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/psb-stage5/plans/${planId}/ytd-status`)
      .then(r => { if (!cancelled) setYtd(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [planId]);

  if (loading) return (
    <Card><CardContent className="py-6 text-center text-xs text-slate-400">
      <Loader2 size={14} className="animate-spin inline mr-2" />Loading YTD state…
    </CardContent></Card>
  );

  const ls = ytd?.live_state || {};
  const sw = ytd?.stage5_written || {};
  const prec = ytd?.precedence;
  const held = Array.isArray(sw.held_fields) ? sw.held_fields : [];

  const metricRows = [
    { label: 'Actual Receipts (Inc VAT)', key: 'actual_receipts_inc_vat', source: 'BARSFILE FYTDTAKINC' },
    { label: 'Result (Inc VAT)', key: 'result_inc_vat', source: 'BARSFILE FYTDRESULT' },
    { label: 'Gross Profit', key: 'gross_profit', source: 'BARSFILE FYTDGROSPR' },
    { label: 'Estimated Receipts (Inc VAT)', key: 'estimated_receipts_inc_vat', source: 'held — no PSB field' },
    { label: 'Management (Inc VAT)', key: 'management_inc_vat', source: 'held — no PSB equivalent' },
  ];

  const precedenceColor = prec === 'imported_fills_missing' ? 'text-emerald-700' : prec === 'stocktake_wins' ? 'text-blue-700' : 'text-slate-600';

  return (
    <Card className="border-purple-200 bg-purple-50/20" data-testid="s5-ytd-write-card">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm flex items-center gap-2 text-purple-900">
          <TrendingUp size={15} className="text-purple-600" />
          YTD Baseline Written to session_previous_ytd
          {ls.source_type === 'psb_s5' && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded ml-1">
              source_type: psb_s5
            </span>
          )}
          {prec && (
            <span className={`text-[10px] font-medium px-1.5 py-0.5 bg-white rounded ml-1 border ${precedenceColor}`}>
              {prec}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        {/* Metric rows */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {metricRows.map(({ label, key, source }) => {
            const isHeld = held.some(h => h.startsWith(key));
            const val = ls[key];
            return (
              <div key={key} className={`rounded p-2 border text-xs ${isHeld ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200 bg-white'}`}
                data-testid={`s5-ytd-field-${key}`}>
                <div className="text-slate-500 mb-0.5">{label}</div>
                <div className={`font-bold tabular-nums ${isHeld ? 'text-amber-700' : 'text-slate-800'}`}>
                  {isHeld ? <span className="italic text-amber-600">blank — unknown</span> : fmtGBP(val)}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">{source}</div>
              </div>
            );
          })}
          <div className="rounded p-2 border border-slate-200 bg-white text-xs" data-testid="s5-ytd-field-ytd_window_start">
            <div className="text-slate-500 mb-0.5">YTD Window Start</div>
            <div className="font-bold text-slate-800">{ls.ytd_window_start || fmtDate(sw.source_fytddate) || '—'}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">BARSFILE FYTDDATE</div>
          </div>
        </div>

        {/* State flags */}
        <div className="flex gap-3 text-xs">
          <div className="flex items-center gap-1">
            <CheckCircle2 size={12} className="text-emerald-500" />
            <span className="text-slate-600">user_edited: <strong>false</strong></span>
          </div>
          {ls.source_type && (
            <div className="flex items-center gap-1">
              <CheckCircle2 size={12} className="text-purple-500" />
              <span className="text-slate-600">source_type: <strong>{ls.source_type}</strong></span>
            </div>
          )}
          {sw.precedence_result && (
            <div className="flex items-center gap-1">
              <CheckCircle2 size={12} className="text-emerald-500" />
              <span className="text-slate-600">precedence: <strong>{sw.precedence_result}</strong></span>
            </div>
          )}
        </div>

        {held.length > 0 && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="s5-ytd-held-notice">
            <strong>Held fields ({held.length}):</strong> These fields have no legacy PSB source equivalent and are stored as NULL.
            They appear blank in the YTD wizard — enter values there before finalising the audit.
          </div>
        )}

        {sessionId && (
          <div className="text-xs text-slate-500 pt-1">
            Edit via the standard YTD panel:{' '}
            <a
              href={`/stocktaking/sessions/${sessionId}`}
              className="text-purple-700 underline"
              data-testid="s5-ytd-session-link"
            >
              Session #{sessionId} → Previous YTD step
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PsbStage5Page() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [reviewTab, setReviewTab] = useState('hida');
  const [applyResult, setApplyResult] = useState(null);
  const [recalcResult, setRecalcResult] = useState(null);
  const [notes, setNotes] = useState('');

  // Initialised flag — prevents double-load race
  const initialisedRef = useRef(false);

  // ── URL restore ─────────────────────────────────────────────────────────────
  const urlPlanId = searchParams.get('planId');

  const restorePlan = useCallback(async (planId) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/psb-stage5/plans/${planId}?tab=hida&page=1&page_size=1`);
      const p = r.data.plan;
      if (p) {
        setPlan(p);
        if (p.status === 'applied') {
          setStep(4);
          // Restore recalc + apply state
          if (p.recalc_stable !== null && p.recalc_stable !== undefined) {
            setRecalcResult({
              stable: p.recalc_stable,
              sha_before: (p.recalc_before_sha || '').slice(0, 16),
              sha_after_recalc_1: (p.recalc_after_sha || '').slice(0, 16),
              sha_after_recalc_2: (p.recalc_after_sha || '').slice(0, 16),
              recalc_1_stable: p.recalc_stable,
              recalc_2_stable: p.recalc_stable,
              evidence: 'Restored from previously persisted check.',
              row_counts_before: p.bridge || {},
            });
          }
          // Load the apply result (YTD write data) from the apply endpoint (idempotent)
          try {
            const ar = await api.post(`/admin/psb-stage5/plans/${planId}/apply`);
            setApplyResult(ar.data);
          } catch {
            setApplyResult({ idempotent: true, status: 'applied', plan_id: planId });
          }
        } else {
          setStep(3);
        }
      }
    } catch { toast.error('Failed to restore plan from URL'); }
    finally { setLoading(false); }
  }, []);

  // ── Boot: load sessions + restore URL ──────────────────────────────────────
  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;

    const boot = async () => {
      setLoading(true);
      try {
        const r = await api.get('/admin/psb-stage5/s2-sessions');
        const sess = r.data.sessions || [];
        setSessions(sess);

        if (urlPlanId) {
          await restorePlan(urlPlanId);
        }
      } catch { toast.error('Failed to load Stage 5 sessions'); }
      finally { setLoading(false); }
    };
    boot();
  }, [urlPlanId, restorePlan]);

  // ── Persist planId in URL ──────────────────────────────────────────────────
  useEffect(() => {
    if (plan?.run_id) {
      setSearchParams({ planId: plan.run_id }, { replace: true });
    }
  }, [plan?.run_id, setSearchParams]);

  // ── Step 1 → 2: create plan ────────────────────────────────────────────────
  const handleCreatePlan = async () => {
    if (!selectedSession) return;
    setLoading(true);
    try {
      const r = await api.post('/admin/psb-stage5/plans', {
        stage0_run_id: selectedSession.stage0_run_id,
        s1_run_id: selectedSession.s1_run_id,
        s2_plan_id: selectedSession.s2_plan_id,
        target_session_id: selectedSession.target_session_id,
        site_id: selectedSession.target_site_id,
        notes: notes || undefined,
      });
      const p = r.data.plan;
      setPlan(p);
      setStep(2);
      if (r.data.idempotent) toast.info('Existing plan restored (idempotent)');
      else toast.success('Stage 5 plan created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create plan');
    }
    setLoading(false);
  };

  // ── Step 3 → 4: apply plan ─────────────────────────────────────────────────
  const handleApply = async () => {
    if (!plan) return;
    setLoading(true);
    try {
      const r = await api.post(`/admin/psb-stage5/plans/${plan.run_id}/apply`);
      setApplyResult(r.data);
      setPlan(prev => ({ ...prev, status: 'applied' }));
      setStep(4);
      if (r.data.idempotent) toast.info('Already applied — idempotent return');
      else toast.success('Stage 5 applied');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    }
    setLoading(false);
  };

  // ── Back nav ───────────────────────────────────────────────────────────────
  const handleBack = () => {
    if (step > 1) setStep(s => s - 1);
    else navigate('/stocktaking/psb-stage4-import');
  };

  // ── Bridge from plan ───────────────────────────────────────────────────────
  const bridge = plan?.bridge || null;

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="psb-stage5-page">

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          data-testid="s5-back-btn"
          className="text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft size={15} className="mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Database size={20} className="text-purple-600" />
            PSB Stage 5 — Route B YTD Baseline &amp; Audit History
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Import HIDAFILE/SUMMFILE/HPLUFILE/BARSFILE provenance · Read-only history evidence · No Ref 067 duplication
          </p>
        </div>
        {plan && (
          <div className="ml-auto flex items-center gap-2">
            <StatusBadge status={plan.status} />
            <span className="text-xs text-slate-400 font-mono">{plan.run_id?.slice(0, 8)}</span>
          </div>
        )}
      </div>

      {/* Step nav */}
      <StepNav current={step} />

      {/* ── Step 1: Source ──────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-4" data-testid="s5-step-source">
          <Card>
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-base">Select Applied Route B Session</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Select an applied Stage 2 Route B Draft session. All fingerprints (stage0_run_id, s1_run_id, s2_plan_id, site, session) are cross-checked before the plan is created.
              </p>
            </CardHeader>
            <CardContent className="px-5 pb-4 space-y-4">
              {loading && !sessions.length ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 size={14} className="animate-spin" /> Loading sessions…
                </div>
              ) : (
                <div className="space-y-2">
                  {sessions.map(s => (
                    <div
                      key={s.s2_plan_id}
                      className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                        selectedSession?.s2_plan_id === s.s2_plan_id
                          ? 'border-purple-400 bg-purple-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      onClick={() => setSelectedSession(s)}
                      data-testid={`s5-session-option-${s.target_session_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-sm text-slate-800">{s.session_reference}</span>
                          <span className="ml-2 text-xs text-slate-400">Session #{s.target_session_id}</span>
                        </div>
                        <StatusBadge status={s.session_status} />
                      </div>
                      <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-x-4 text-xs text-slate-500">
                        <span>Site: {s.target_site_id}</span>
                        <span>Period: {s.period_from} → {s.period_to}</span>
                        <span>Stage 0: <span className="font-mono">{s.stage0_run_id?.slice(0, 10)}…</span></span>
                        <span>S2: <span className="font-mono">{s.s2_plan_id?.slice(0, 8)}</span></span>
                      </div>
                    </div>
                  ))}
                  {sessions.length === 0 && !loading && (
                    <p className="text-sm text-slate-400 text-center py-4">No applied Route B Stage 2 sessions found.</p>
                  )}
                </div>
              )}

              {selectedSession && (
                <div className="pt-2">
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="PSB Stage 5 import notes…"
                    data-testid="s5-notes-input"
                  />
                </div>
              )}

              <Button
                className="w-full"
                disabled={!selectedSession || loading}
                onClick={handleCreatePlan}
                data-testid="s5-create-plan-btn"
              >
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Database size={14} className="mr-2" />}
                Build Stage 5 Plan
              </Button>
            </CardContent>
          </Card>

          {/* Info box */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700 flex gap-2">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            <div>
              <strong>What Stage 5 imports:</strong> HIDAFILE (64 periods, refs 3–66), SUMMFILE (16 periods, refs 51–66),
              HPLUFILE (2,205 product rows, refs 52–66), BARSFILE (1 YTD bar row), HOUSEINF (site header).
              All prior history is read-only evidence. Current Ref 067 is never duplicated.
              Percentage fields are preserved verbatim. TAKINGS totals are evidence-only (Stage 6 boundary).
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Matrix ──────────────────────────────────────────────────── */}
      {step === 2 && plan && (
        <div className="space-y-4" data-testid="s5-step-matrix">
          <SourceBanner session={selectedSession || {
            session_reference: `Session #${plan.target_session_id}`,
            target_site_id: plan.target_site_id,
            period_from: plan.bridge?.houseinf_summary?.opendate,
            period_to: plan.bridge?.houseinf_summary?.valudate,
            stage0_run_id: plan.stage0_run_id,
            s2_plan_id: plan.s2_plan_id,
          }} />

          {/* Semantic notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-purple-800">
              <div className="font-semibold flex items-center gap-1 mb-1"><ShieldAlert size={12} /> Precedence Rule</div>
              AbbeyZone Stocktake session data takes strict precedence over any legacy imported value.
              Current Ref {bridge?.current_ref} is absent from all source files — no duplication possible.
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
              <div className="font-semibold flex items-center gap-1 mb-1"><AlertTriangle size={12} /> Stage 6 Boundary</div>
              SUMMFILE TAKEEXC/TAKEINC and BARSFILE FYTD takings are stored as evidence only.
              Raw TAKEFILE transactions remain Stage 6 scope — no double posting.
            </div>
          </div>

          {/* Bridge summary */}
          <BridgeSummary bridge={bridge} />

          {/* HOUSEINF metadata */}
          {bridge?.houseinf_summary && (
            <Card className="border-slate-200">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <BookOpen size={13} /> HOUSEINF — Site Header
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  {[
                    ['HSE', bridge.houseinf_summary.hse],
                    ['Name', bridge.houseinf_summary.name],
                    ['Location', bridge.houseinf_summary.location],
                    ['Current Ref', bridge.houseinf_summary.ref],
                    ['Period Open', bridge.houseinf_summary.opendate],
                    ['Valuation Date', bridge.houseinf_summary.valudate],
                    ['Year End', bridge.houseinf_summary.yearend],
                    ['History Count', bridge.houseinf_summary.history_count],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="text-slate-500">{k}</div>
                      <div className="font-medium text-slate-800">{v ?? '—'}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Completeness matrix */}
          <CompletenessMatrix bridge={bridge} />

          {/* Navigation */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="s5-back-to-source-btn">Back</Button>
            <Button className="flex-1" onClick={() => setStep(3)} data-testid="s5-proceed-to-review-btn">
              Proceed to Review <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 3: Review ──────────────────────────────────────────────────── */}
      {step === 3 && plan && (
        <div className="space-y-4" data-testid="s5-step-review">
          <SourceBanner session={selectedSession || {
            session_reference: `Session #${plan.target_session_id}`,
            target_site_id: plan.target_site_id,
            period_from: plan.bridge?.houseinf_summary?.opendate,
            period_to: plan.bridge?.houseinf_summary?.valudate,
            stage0_run_id: plan.stage0_run_id,
            s2_plan_id: plan.s2_plan_id,
          }} />

          {/* Tab bar */}
          <div className="flex gap-0.5 border border-slate-200 rounded-lg overflow-hidden w-fit" data-testid="s5-review-tabs">
            {[
              { id: 'hida', label: `HIDA (${bridge?.hida_refs_total ?? '—'})`, icon: Calendar },
              { id: 'summ', label: `SUMM (${bridge?.summ_refs_total ?? '—'})`, icon: Table2 },
              { id: 'hplu', label: `HPLU (${bridge?.hplu_product_rows_total?.toLocaleString() ?? '—'})`, icon: Package },
              { id: 'barsfile', label: 'BARSFILE YTD', icon: TrendingUp },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${
                  reviewTab === id
                    ? 'bg-slate-800 text-white'
                    : 'bg-white text-slate-500 hover:bg-slate-50'
                }`}
                onClick={() => setReviewTab(id)}
                data-testid={`s5-tab-${id}`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="rounded-lg border border-slate-200 p-4">
            {reviewTab === 'hida' && <HidaTab planId={plan.run_id} totalCount={bridge?.hida_refs_total} />}
            {reviewTab === 'summ' && <SummTab planId={plan.run_id} />}
            {reviewTab === 'hplu' && <HpluTab planId={plan.run_id} bridge={bridge} />}
            {reviewTab === 'barsfile' && <BarsfileTab planId={plan.run_id} />}
          </div>

          {/* Navigation */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)} data-testid="s5-back-to-matrix-btn">Back</Button>
            <Button
              className="flex-1"
              onClick={handleApply}
              disabled={loading || plan.status === 'applied'}
              data-testid="s5-apply-btn"
            >
              {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />}
              {plan.status === 'applied' ? 'Already Applied' : 'Apply Stage 5 History Import'}
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 4: Apply + Evidence ────────────────────────────────────────── */}
      {step === 4 && plan && (
        <div className="space-y-4" data-testid="s5-step-apply">
          {/* Apply result */}
          <Card className="border-emerald-300 bg-emerald-50/30">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2 text-emerald-800">
                <CheckCircle2 size={16} className="text-emerald-600" />
                Stage 5 Applied — History Evidence Persisted
                {applyResult?.idempotent && <Badge variant="outline" className="ml-2 text-xs bg-white border-slate-300 text-slate-500">idempotent</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs mb-3">
                {[
                  { label: 'Plan ID', value: plan.run_id?.slice(0, 16) + '…' },
                  { label: 'Session', value: `#${plan.target_session_id}` },
                  { label: 'Applied At', value: plan.applied_at?.split('T')[0] ?? '—' },
                  { label: 'HIDA rows', value: bridge?.hida_refs_total },
                  { label: 'SUMM rows', value: bridge?.summ_refs_total },
                  { label: 'HPLU rows', value: bridge?.hplu_product_rows_total?.toLocaleString() },
                  { label: 'BARSFILE rows', value: bridge?.barsfile_bars },
                  { label: 'FYTD Date', value: bridge?.barsfile_fytddate },
                  { label: 'FYTD Takings Inc', value: bridge?.barsfile_fytdtakinc ? `£${parseFloat(bridge.barsfile_fytdtakinc).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-white rounded p-2 border border-slate-200">
                    <div className="font-bold text-slate-800">{value ?? '—'}</div>
                    <div className="text-slate-500">{label}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={12} className="text-emerald-500" />
                  Prior history stored as read-only provenance evidence (refs 3–66)
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={12} className="text-emerald-500" />
                  Current Ref {bridge?.current_ref} not duplicated — AbbeyZone session precedence maintained
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={12} className="text-emerald-500" />
                  Percentage fields preserved verbatim; Stage 6 boundary flags applied
                </div>
              </div>
            </CardContent>
          </Card>

          {/* YTD Baseline written to session_previous_ytd */}
          <YtdWriteCard planId={plan.run_id} sessionId={plan.target_session_id} />

          {/* Recalc check evidence gate */}
          <RecalcCheckCard
            planId={plan.run_id}
            onComplete={r => {
              setRecalcResult(r);
              // Update plan with recalc results
              setPlan(prev => ({
                ...prev,
                recalc_stable: r.stable,
                recalc_before_sha: r.sha_before,
                recalc_after_sha: r.sha_after_recalc_2,
              }));
            }}
          />

          {/* Browse history link */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(3)} data-testid="s5-back-to-review-btn">
              <ArrowLeft size={14} className="mr-1" /> Review History
            </Button>
            <Button variant="outline" onClick={() => setStep(2)} data-testid="s5-back-to-matrix-btn-2">
              Matrix View
            </Button>
            {recalcResult?.stable && (
              <div className="ml-auto flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                <CheckCircle2 size={14} />
                Stage 5 evidence ledger complete
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
